import moment from 'moment'
import { useCallback, useState } from 'react'

import { Option } from './SelectionFilters'
import { InventoryHealthUrlState } from './useInventoryHealthUrlState'

const DEFAULT_DAYS = 14

export type QueryFilters = {
  accommodation: Option | null
  provider: Option | null
  channel: Option | null
  from: string
  to: string
  differencesOnly: boolean
}

function defaults(initial: InventoryHealthUrlState): QueryFilters {
  return {
    accommodation: initial.propertyCode
      ? { value: initial.propertyCode, label: initial.propertyCode }
      : null,
    provider: initial.providerCode
      ? { value: initial.providerCode, label: initial.providerCode }
      : null,
    channel: initial.channelID
      ? { value: initial.channelID, label: initial.channelID }
      : null,
    from: initial.from ?? moment().format('YYYY-MM-DD'),
    to: initial.to ?? moment().add(DEFAULT_DAYS, 'days').format('YYYY-MM-DD'),
    differencesOnly: initial.differencesOnly ?? false
  }
}

/**
 * The parameters that decide what gets FETCHED, held as one value.
 *
 * They were six independent useStates drilled through the panel as twelve
 * props. Grouping them keeps index.tsx composing rather than plumbing, and
 * means adding a parameter is one field rather than one field plus two props
 * plus a setter.
 *
 * URL codes are restored as temporary Options so a copied diagnostic can run
 * immediately. SelectionFilters replaces the temporary labels with the names
 * returned by its option queries as soon as they arrive.
 */
export function useInventoryHealthQueryFilters(
  initial: InventoryHealthUrlState
) {
  const [filters, setFilters] = useState<QueryFilters>(() => defaults(initial))

  const set = useCallback(
    <K extends keyof QueryFilters>(key: K, value: QueryFilters[K]) =>
      setFilters((current) => ({ ...current, [key]: value })),
    []
  )

  // Reset clears the fields; it does NOT re-query (§3).
  const reset = useCallback(
    () =>
      setFilters((current) => ({
        ...defaults({}),
        accommodation: current.accommodation,
        provider: current.provider
      })),
    []
  )

  return { filters, set, reset }
}
